import React, { useState } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useQuery } from 'react-query';
import { v1Backend } from 'services/api/client';
import PageLoader from './LoadingButton/PageLoader';
import { STATUS_TO_PATH } from 'utils/onboardingRoute';
import { isMembershipFinalizingPath } from 'utils/dashboardPageAccess';
import BankDetailsRequiredDialog from './BrandWorldPayment/BankDetailsRequiredDialog';

/**
 * SECURITY (VAPT rescan PT-29 — onboarding workflow bypass via direct /home access).
 *
 * A company that has NOT finished onboarding (e.g. stopped after PAN, before Terms
 * Acceptance / Plan Selection) could type /home directly and reach the dashboard.
 * The onboarding step pages are guarded (OnboardingStepGuard) but /home was not,
 * so this guard bounces an incomplete company back to its current onboarding step.
 *
 * Distinguishes three cases from the fresh `onboard-company` status:
 *   - No company session (guest / unauthenticated): render — ProtectedRoute and
 *     the public-browse rules already decide what a guest may see.
 *   - Completed company (ONBOARDING_COMPLETE -> /home): render.
 *   - Incomplete onboarding status (GST/BANK_DETAILS/TNC/PAYMENT/... -> a non-/home
 *     onboarding route): redirect to that step.
 */
const OnboardingCompleteGuard = ({ children }) => {
  const location = useLocation();
  const [bankSaved, setBankSaved] = useState(false);

  const { data, isLoading, isError } = useQuery(
    ['onboarding-complete-guard'],
    async () => {
      const res = await v1Backend.get('onboard-company');
      return res?.data ?? null;
    },
    {
      staleTime: 0,
      cacheTime: 0,
      refetchOnMount: 'always',
      refetchOnWindowFocus: false,
      retry: 1,
    }
  );

  if (isLoading) return <PageLoader />;

  // Could not resolve a company (guest, or request failed): don't hijack — let
  // ProtectedRoute / public-browse handling take over.
  const hasCompanySession = Boolean(
    data && (data._id || data.companyOnboardingStatus)
  );
  if (isError || !hasCompanySession) return children;

  const status = data.companyOnboardingStatus;
  const canonicalPath = status ? STATUS_TO_PATH[status] : undefined;

  // Completed (maps to /home) or unknown status: allow — but a completed company
  // must have a bank account on file before using the dashboard (seller payouts
  // go there). The popup blocks until it is saved.
  if (!canonicalPath || canonicalPath === '/home') {
    const needsBank =
      status === 'ONBOARDING_COMPLETE' &&
      !bankSaved &&
      !(data.bankdetails?.bankAccountNo && data.bankdetails?.ifsc);
    return (
      <>
        {children}
        {needsBank ? (
          <BankDetailsRequiredDialog company={data} onSaved={() => setBankSaved(true)} />
        ) : null}
      </>
    );
  }

  // NO public-browse exemption here. `isPublicBrowsePath('/home')` is true (the
  // marketplace landing is guest-viewable), so exempting it let an authenticated
  // but incomplete company reach the dashboard by typing exactly /home — the
  // bypass VAPT Finding 29 reproduces (/bank -> /home). Deeper /home/* pages did
  // redirect, which is why this looked fixed. Guests never reach this branch:
  // they have no company session and returned above. Guest browsing keeps working
  // through /explore, which is not wrapped by this guard.

  // The Juspay membership return page is the finalizer that activates the plan and
  // flips onboarding to ONBOARDING_COMPLETE. During onboarding the status is still
  // PAYMENT/MEMBERSHIP (-> /pricing) when the gateway returns here, so without this
  // exemption the guard bounces the return to /pricing before the finalizer can run
  // — the plan never activates and no invoice is generated ("Buy Now" stays put).
  // Matches the same exemption ProtectedRoute and Sidebar already apply.
  if (isMembershipFinalizingPath(location.pathname, location.search)) {
    return children;
  }

  return <Navigate to={canonicalPath} replace />;
};

export default OnboardingCompleteGuard;
